/**
 * Eval Scores Sidebar — right-side Sheet displaying failure patterns
 * and baseline rubric scores from the optimization workflow.
 *
 * Opened via a trigger button in the review phase. Uses the existing
 * Sheet primitive for consistent overlay behaviour.
 */

import React, { useState } from 'react';
import { BarChart3, ChevronDown, AlertCircle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible';
import type { FailurePattern, BaselineScoreSummary } from '@/lib/api/versionWorkflow';
import type { RubricOverviewItem } from '@/lib/api/performance';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patterns: FailurePattern[];
  baselineScores: BaselineScoreSummary | null;
  promptVersion: string | null;
  /** Optional: full rubric data from eval overview (insights mode) */
  evalRubrics?: RubricOverviewItem[];
  /** Optional: label for eval set header in insights mode */
  evalSetName?: string | null;
}

function scoreColor(score: number): string {
  if (score >= 70) return 'bg-green-500 dark:bg-green-500';
  if (score >= 50) return 'bg-amber-500 dark:bg-amber-500';
  return 'bg-red-500 dark:bg-red-500';
}

function scoreTextColor(score: number): string {
  if (score >= 70) return 'text-green-600 dark:text-green-400';
  if (score >= 50) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

const SCORE_THRESHOLD = 50.0;

const EvalScoresSidebar: React.FC<Props> = ({
  open,
  onOpenChange,
  patterns,
  baselineScores,
  promptVersion,
  evalRubrics,
  evalSetName,
}) => {
  // Use evalRubrics if provided (insights mode), otherwise fall back to baselineScores
  const isInsightsMode = evalRubrics != null && evalRubrics.length > 0;

  const scoreEntries: [string, number][] = isInsightsMode
    ? evalRubrics
        .filter((r) => r.score_7d != null)
        .sort((a, b) => (a.score_7d ?? 0) - (b.score_7d ?? 0))
        .map((r) => [r.name, r.score_7d as number])
    : baselineScores?.score_averages
      ? Object.entries(baselineScores.score_averages)
          .filter((entry): entry is [string, number] => entry[1] != null)
          .sort((a, b) => b[1] - a[1])
      : [];

  const lowScoreEntries = scoreEntries.filter(([, score]) => score < SCORE_THRESHOLD);

  const headerLabel = isInsightsMode
    ? evalSetName || 'Eval Scores'
    : promptVersion
      ? `v${promptVersion} Eval Scores`
      : 'Eval Scores';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/40">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            <SheetTitle className="text-base">{headerLabel}</SheetTitle>
          </div>
          <SheetDescription>Latest simulation run</SheetDescription>
        </SheetHeader>

        <div className="px-6 py-5 space-y-6">
          {/* ── Failure Patterns ─────────────────────────────────── */}
          {patterns.length > 0 && (
            <div className="space-y-3">
              {patterns.map((pattern) => (
                <PatternCard key={pattern.pattern_id} pattern={pattern} />
              ))}
            </div>
          )}

          {/* ── Rubric Scores ───────────────────────────────────── */}
          {scoreEntries.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-heading">All Rubric Scores</h3>

              {/* Low-score warning */}
              {lowScoreEntries.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50/60 dark:bg-red-950/20 border border-red-200/60 dark:border-red-800/30 px-3 py-2.5">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-red-700 dark:text-red-400">
                      {lowScoreEntries.length} Rubric{lowScoreEntries.length > 1 ? 's' : ''} below
                      threshold ({SCORE_THRESHOLD.toFixed(0)}) - Need Attention
                    </p>
                    <p className="text-xs text-red-600/80 dark:text-red-400/70 mt-0.5">
                      {lowScoreEntries.map(([name]) =>
                        isInsightsMode ? name : formatScoreName(name),
                      ).join(', ')}
                    </p>
                  </div>
                </div>
              )}

              {/* Score bars */}
              <div className="space-y-3">
                {scoreEntries.map(([name, score]) => (
                  <ScoreBar
                    key={name}
                    name={isInsightsMode ? name : formatScoreName(name)}
                    score={score}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {patterns.length === 0 && scoreEntries.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No patterns or scores available.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* ── Pattern Card (collapsible) ──────────────────────────────────────────── */

const PatternCard: React.FC<{ pattern: FailurePattern }> = ({ pattern }) => {
  const [open, setOpen] = useState(false);
  const hasDetails = pattern.example_reasoning.length > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className="w-full text-left rounded-lg border border-border/60 bg-card px-4 py-3 hover:bg-muted/30 transition-colors"
        disabled={!hasDetails}
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0 text-amber-500">
            <AlertCircle className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-sm font-medium text-heading">
              {pattern.pattern_name}
            </span>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              {pattern.evaluators_affected.length > 0
                ? `${pattern.evaluators_affected.join(', ')} · `
                : ''}
              {pattern.description}
            </p>
          </div>
          {hasDetails && (
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground shrink-0 mt-0.5 transition-transform duration-200 ${
                open ? 'rotate-180' : ''
              }`}
            />
          )}
        </div>
      </CollapsibleTrigger>

      {hasDetails && (
        <CollapsibleContent>
          <div className="px-4 pb-3 pt-1 space-y-2 border-x border-b border-border/60 rounded-b-lg bg-card">
            {pattern.example_reasoning.map((reasoning, idx) => (
              <p
                key={idx}
                className="text-xs text-muted-foreground/80 leading-relaxed pl-7"
              >
                {reasoning}
              </p>
            ))}
          </div>
        </CollapsibleContent>
      )}
    </Collapsible>
  );
};

/* ── Score Bar ────────────────────────────────────────────────────────────── */

const ScoreBar: React.FC<{ name: string; score: number }> = ({ name, score }) => {
  const pct = Math.min(Math.max(score, 0), 100);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-sm text-body">{name}</span>
        <span className={`text-sm font-medium tabular-nums ${scoreTextColor(score)}`}>
          {score.toFixed(1)}/100
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${scoreColor(score)}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

/* ── Helpers ──────────────────────────────────────────────────────────────── */

function formatScoreName(key: string): string {
  return key
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default EvalScoresSidebar;
